import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type {
  AddFlowEtfInput,
  AddStockGroupInput,
  FlowEtfDto,
  MarketScreenDto,
  SaveScreenInput,
  ScanSettings,
  ScanSettingsDto,
  StockGroupDto,
} from 'shared';

import { apiClient } from '../../lib/api/client';

import { marketConsoleKeys } from './queries';

// The watchlist is owned by Market Health; its cached queries share this key prefix.
const MARKET_HEALTH_KEY = ['market-health'] as const;

export interface WatchToggleInput {
  symbol: string;
  watched: boolean;
}

function apiErrorMessage(error: unknown): string | undefined {
  return axios.isAxiosError<{ error?: { message?: string } }>(error)
    ? error.response?.data?.error?.message
    : undefined;
}

async function toggleWatch({ symbol, watched }: WatchToggleInput): Promise<void> {
  try {
    if (watched) {
      await apiClient.delete(`/market-health/watchlist/${encodeURIComponent(symbol)}`);
    } else {
      await apiClient.post('/market-health/watchlist', { symbol });
    }
  } catch (error) {
    const message = apiErrorMessage(error);
    throw message ? new Error(message) : error;
  }
}

export function useToggleWatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleWatch,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: marketConsoleKeys.all }),
        queryClient.invalidateQueries({ queryKey: MARKET_HEALTH_KEY }),
      ]);
    },
  });
}

async function withApiMessage<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    const message = apiErrorMessage(error);
    throw message ? new Error(message) : error;
  }
}

function useFlowMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketConsoleKeys.flow });
    },
  });
}

export function useAddFlowEtf() {
  return useFlowMutation((input: AddFlowEtfInput) =>
    withApiMessage(async () => {
      const { data } = await apiClient.post<{ etf: FlowEtfDto }>(
        '/market-console/flow/etfs',
        input,
      );
      return data.etf;
    }),
  );
}

export function useRemoveFlowEtf() {
  return useFlowMutation((id: string) =>
    withApiMessage(async () => {
      await apiClient.delete(`/market-console/flow/etfs/${encodeURIComponent(id)}`);
    }),
  );
}

export function useResetFlowEtfs() {
  return useFlowMutation(() =>
    withApiMessage(async () => {
      await apiClient.post('/market-console/flow/etfs/reset');
    }),
  );
}

/** Thresholds change every stock-derived view, so the whole console is refetched. */
function useConsoleMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketConsoleKeys.all });
    },
  });
}

export function useSaveScanSettings() {
  return useConsoleMutation((settings: ScanSettings) =>
    withApiMessage(async () => {
      const { data } = await apiClient.put<ScanSettingsDto>(
        '/market-console/scan-settings',
        settings,
      );
      return data;
    }),
  );
}

export function useResetScanSettings() {
  return useConsoleMutation(() =>
    withApiMessage(async () => {
      const { data } = await apiClient.delete<ScanSettingsDto>('/market-console/scan-settings');
      return data;
    }),
  );
}

export function useCreateScreen() {
  return useConsoleMutation((input: SaveScreenInput) =>
    withApiMessage(async () => {
      const { data } = await apiClient.post<{ screen: MarketScreenDto }>(
        '/market-console/screens',
        input,
      );
      return data.screen;
    }),
  );
}

export function useUpdateScreen() {
  return useConsoleMutation(({ id, input }: { id: string; input: SaveScreenInput }) =>
    withApiMessage(async () => {
      const { data } = await apiClient.put<{ screen: MarketScreenDto }>(
        `/market-console/screens/${encodeURIComponent(id)}`,
        input,
      );
      return data.screen;
    }),
  );
}

export function useDeleteScreen() {
  return useConsoleMutation((id: string) =>
    withApiMessage(async () => {
      await apiClient.delete(`/market-console/screens/${encodeURIComponent(id)}`);
    }),
  );
}

export function useAddStockGroup() {
  return useConsoleMutation((input: AddStockGroupInput) =>
    withApiMessage(async () => {
      const { data } = await apiClient.post<{ group: StockGroupDto }>(
        '/market-console/stock-groups',
        input,
      );
      return data.group;
    }),
  );
}

export function useRemoveStockGroup() {
  return useConsoleMutation((id: string) =>
    withApiMessage(async () => {
      await apiClient.delete(`/market-console/stock-groups/${encodeURIComponent(id)}`);
    }),
  );
}
